import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign,
  Headphones,
  TrendingUp,
  Handshake,
  Mail,
  CheckCircle2,
  X,
  ArrowRight,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/Skeleton';
import { usePriorityStore } from '@/stores/priorityStore';
import { useAuthStore } from '@/stores/authStore';
import { useDashboardStore } from '@/stores/dashboardStore';
import type { PriorityItem } from '@/stores/priorityStore';

const typeIcons: Record<string, typeof DollarSign> = {
  overdue_invoice: DollarSign,
  urgent_ticket: Headphones,
  new_signal: TrendingUp,
  partnership_checkin: Handshake,
  unread_email: Mail,
};

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

function priorityColor(priority: number): string {
  if (priority >= 80) return 'bg-red-500';
  if (priority >= 60) return 'bg-amber-500';
  return 'bg-blue-500';
}

function priorityBadgeClasses(priority: number): string {
  if (priority >= 80) return 'bg-red-500/10 text-red-400 border-red-500/20';
  if (priority >= 60) return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
  return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
}

function PriorityCard({
  item,
  onDismiss,
  onAction,
}: {
  item: PriorityItem;
  onDismiss: () => void;
  onAction: () => void;
}) {
  const Icon = typeIcons[item.type] ?? Mail;

  return (
    <div className="glass-card rounded-lg overflow-hidden flex group">
      {/* Priority indicator bar */}
      <div className={`w-1 flex-shrink-0 ${priorityColor(item.priority)}`} />

      <div className="flex-1 px-5 py-4 flex items-start gap-4">
        {/* Icon */}
        <div className="mt-0.5 p-2 rounded-lg bg-surface-800 border border-surface-700 flex-shrink-0">
          <Icon className="h-4 w-4 text-surface-400" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-sm font-semibold text-surface-200 truncate">
              {item.title}
            </h3>
            <span
              className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${priorityBadgeClasses(item.priority)}`}
            >
              {item.priority}
            </span>
          </div>
          <p className="text-xs text-surface-400 line-clamp-2">{item.description}</p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0 mt-0.5">
          <button
            onClick={onAction}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-nexus-400 bg-nexus-600/10 border border-nexus-500/20 rounded-lg hover:bg-nexus-600/20 transition-colors cursor-pointer"
          >
            {item.actionLabel}
            <ArrowRight className="h-3 w-3" />
          </button>
          <button
            onClick={onDismiss}
            className="p-1.5 rounded-lg text-surface-600 hover:text-surface-300 hover:bg-surface-700 transition-colors cursor-pointer opacity-0 group-hover:opacity-100"
            title="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

export function Today() {
  const navigate = useNavigate();
  const { items, isLoading, error, fetch, dismiss } = usePriorityStore();
  const { user } = useAuthStore();
  const { stats, fetchDashboard } = useDashboardStore();

  useEffect(() => {
    fetch();
    if (!stats) fetchDashboard();
  }, []);

  const firstName = user?.name?.split(' ')[0] || 'there';

  // Loading state
  if (isLoading && items.length === 0) {
    return (
      <div className="relative max-w-[1440px] space-y-6">
        {/* Greeting skeleton */}
        <div>
          <Skeleton className="h-8 w-72 mb-2" />
          <Skeleton className="h-4 w-48" />
        </div>
        {/* Quick stats skeleton */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-lg border border-surface-700 bg-surface-800/60 p-5">
              <Skeleton className="h-3 w-20 mb-3" />
              <Skeleton className="h-7 w-12" />
            </div>
          ))}
        </div>
        {/* Card skeletons */}
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-lg border border-surface-700 bg-surface-800/60 p-5 flex items-center gap-4">
              <Skeleton className="h-8 w-8 rounded-lg" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-3 w-2/3" />
              </div>
              <Skeleton className="h-8 w-24 rounded-lg" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error && items.length === 0) {
    return (
      <div className="relative max-w-[1440px]">
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <h3 className="text-lg font-semibold text-surface-200">Failed to load priorities</h3>
          <p className="mt-1 text-sm text-surface-400">{error}</p>
          <button
            onClick={() => fetch()}
            className="mt-4 px-4 py-2 bg-nexus-600 text-white rounded-lg text-sm font-medium hover:bg-nexus-500 transition-colors cursor-pointer"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const quickStats = [
    { label: 'Open Tickets', value: stats?.openTickets ?? 0 },
    { label: 'Pending Invoices', value: stats?.pendingInvoices ?? 0 },
    { label: 'Growth Signals', value: stats?.growthSignals ?? 0 },
    { label: 'Active Partners', value: stats?.activePartnerships ?? 0 },
  ];

  return (
    <div className="relative max-w-[1440px] space-y-6">
      {/* Ambient orbs */}
      <div className="orb orb-green w-[500px] h-[500px] -top-40 -left-40 opacity-60" />
      <div className="orb orb-blue w-[400px] h-[400px] top-60 -right-32 opacity-40" />

      {/* Greeting */}
      <div className="relative z-10">
        <h1 className="text-2xl font-bold text-surface-100 tracking-tight">
          {getGreeting()}, {firstName}
        </h1>
        <p className="text-sm text-surface-500 mt-1">
          {items.length > 0
            ? `You have ${items.length} item${items.length !== 1 ? 's' : ''} that need${items.length === 1 ? 's' : ''} attention`
            : 'Nothing urgent right now'}
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 relative z-10">
        {quickStats.map((stat) => (
          <div key={stat.label} className="glass-stat rounded-lg p-4">
            <p className="text-[11px] font-medium text-surface-500 tracking-wide uppercase mb-1">
              {stat.label}
            </p>
            <p className="text-xl font-bold text-surface-100 tracking-tight">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Priority Feed */}
      <div className="relative z-10">
        <h2 className="section-title section-title-glow mb-4">Priority Feed</h2>

        {items.length === 0 ? (
          <div className="glass-card rounded-lg py-16 flex flex-col items-center justify-center text-center">
            <div className="p-4 mb-4 bg-emerald-500/10 rounded-full">
              <CheckCircle2 className="h-8 w-8 text-emerald-400" />
            </div>
            <h3 className="text-lg font-semibold text-surface-200">All caught up!</h3>
            <p className="mt-1 text-sm text-surface-400 max-w-sm">
              No urgent items need your attention right now. Great work.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item) => (
              <PriorityCard
                key={item.id}
                item={item}
                onDismiss={() => dismiss(item.id)}
                onAction={() => navigate(item.actionUrl)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
